import React, { useState, useContext, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { ThemeContext } from '../contexts/ThemeContext';

const KonselingPage = () => {
  const { darkMode } = useContext(ThemeContext);
  // Get real teacher name from localStorage user data
  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  const teacherName = userData.name || 'Guru BK';
  const [showStudentSuggestions, setShowStudentSuggestions] = useState(false);
  const studentSuggestionRef = useRef(null);
  
  // Student options from API
  const [studentOptions, setStudentOptions] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentError, setStudentError] = useState('');

  // Fetch students from API on mount
  useEffect(() => {
    const fetchStudents = async () => {
      setLoadingStudents(true);
      setStudentError('');
      try {
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/api/kts-students`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Gagal memuat data siswa');
        const data = await response.json();
        if (data.success) {
          setStudentOptions(data.data);
        } else {
          setStudentError('Gagal memuat data siswa');
        }
      } catch (err) {
        setStudentError('Gagal memuat data siswa: ' + err.message);
      } finally {
        setLoadingStudents(false);
      }
    };
    fetchStudents();
    // Existing click outside logic
    const handleClickOutside = (event) => {
      if (studentSuggestionRef.current && !studentSuggestionRef.current.contains(event.target)) {
        setShowStudentSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const [formData, setFormData] = useState({
    studentName: '',
    studentNIS: '',
    studentKelas: '',
    teacherName: teacherName,
    counselingType: '',
    date: '',
    time: '',
    notes: ''
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [filteredStudents, setFilteredStudents] = useState([]);
  
  useEffect(() => {
    // Add click outside listener to close suggestions
    const handleClickOutside = (event) => {
      if (studentSuggestionRef.current && !studentSuggestionRef.current.contains(event.target)) {
        setShowStudentSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));

    // Filter suggestions for student name field
    if (name === 'studentName') {
      const filtered = studentOptions.filter(student =>
        (student.name || '').toLowerCase().includes(value.toLowerCase())
      );
      setFilteredStudents(filtered);
      setShowStudentSuggestions(true);
    }
  };

  const handleStudentClick = (student) => {
    setFormData(prevData => ({
      ...prevData,
      studentName: student.name,
      studentNIS: student.nis,
      studentKelas: student.kelas
    }));
    setShowStudentSuggestions(false);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setNotification({ show: false, message: '', type: 'success' });
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/api/konseling`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        let errorMsg = 'Gagal mengirim data konseling';
        try {
          const errData = await response.json();
          if (errData && errData.message) errorMsg = errData.message;
        } catch {}
        setNotification({ show: true, message: errorMsg, type: 'error' });
        setSubmitting(false);
        return;
      }
      setSubmitted(true);
      setNotification({ show: true, message: 'Data konseling berhasil disimpan!', type: 'success' });
      setFormData({
        studentName: '',
        studentNIS: '',
        studentKelas: '',
        teacherName: teacherName,
        counselingType: '',
        date: '',
        time: '',
        notes: ''
      });
      setTimeout(() => {
        setSubmitted(false);
      }, 3000);
    } catch (err) {
      setNotification({ show: true, message: 'Terjadi kesalahan jaringan: ' + err.message, type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <PageContainer>
      <PageHeader>
        <h1>Konseling BK</h1>
        <p>Layanan konseling dan bimbingan siswa</p>
      </PageHeader>
      
      {notification.show && (
        <NotificationToast $type={notification.type}>
          <NotificationMessage>{notification.message}</NotificationMessage>
          <CloseNotificationButton onClick={() => setNotification({ ...notification, show: false })}>
            ×
          </CloseNotificationButton>
        </NotificationToast>
      )}
      {submitted ? (
        <SuccessMessage>
          <SuccessIcon>✓</SuccessIcon>
          <div>
            <h3>Formulir Berhasil Dikirim!</h3>
            <p>Data konseling telah berhasil disimpan.</p>
          </div>
        </SuccessMessage>
      ) : (
        <FormContainer>
          <FormWrapper theme={darkMode ? 'dark' : 'light'}>
            <form onSubmit={handleSubmit}>
              <FormGroup theme={darkMode ? 'dark' : 'light'} ref={studentSuggestionRef}>
                <label htmlFor="studentName">Nama Siswa</label>
                <input
                  type="text"
                  id="studentName"
                  name="studentName"
                  value={formData.studentName}
                  onChange={handleChange}
                  placeholder="Masukkan nama siswa"
                  required
                  autoComplete="off"
                  disabled={loadingStudents}
                />
                {loadingStudents && (
                  <div style={{ padding: '0.5rem', color: '#888' }}>Memuat data siswa...</div>
                )}
                {studentError && (
                  <div style={{ padding: '0.5rem', color: '#e74c3c' }}>{studentError}</div>
                )}
                {showStudentSuggestions && filteredStudents.length > 0 && (
                  <SuggestionsList theme={darkMode ? 'dark' : 'light'}>
                    {filteredStudents.map((student, index) => (
                      <StudentSuggestionItem
                        key={student.nis || index}
                        onClick={() => handleStudentClick(student)}
                        theme={darkMode ? 'dark' : 'light'}
                      >
                        <div className="student-name">{student.name}</div>
                        <div className="student-info">
                          {student.kelas} - NIS: {student.nis}
                        </div>
                      </StudentSuggestionItem>
                    ))}
                  </SuggestionsList>
                )}
              </FormGroup>

              {formData.studentKelas && (
                <FormRow>
                  <FormGroup theme={darkMode ? 'dark' : 'light'}>
                    <label>Kelas</label>
                    <input
                      type="text"
                      value={formData.studentKelas}
                      disabled
                      readOnly
                    />
                  </FormGroup>
                  <FormGroup theme={darkMode ? 'dark' : 'light'}>
                    <label>NIS</label>
                    <input
                      type="text"
                      value={formData.studentNIS}
                      disabled
                      readOnly
                    />
                  </FormGroup>
                </FormRow>
              )}
              
              <FormGroup theme={darkMode ? 'dark' : 'light'}>
                <label htmlFor="teacherName">BK Pendamping</label>
                <input
                  type="text"
                  id="teacherName"
                  name="teacherName"
                  value={formData.teacherName}
                  onChange={handleChange}
                  placeholder="Nama guru BK pendamping"
                  readOnly
                  disabled
                />
              </FormGroup>
              
              <FormGroup theme={darkMode ? 'dark' : 'light'}>
                <label htmlFor="counselingType">Jenis Pendampingan</label>
                <input
                  type="text"
                  id="counselingType"
                  name="counselingType"
                  value={formData.counselingType}
                  onChange={handleChange}
                  placeholder="Contoh: Akademik, Karir, Pribadi"
                  required
                />
              </FormGroup>
              
              <FormRow>
                <FormGroup theme={darkMode ? 'dark' : 'light'}>
                  <label htmlFor="date">Tanggal</label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>
                
                <FormGroup theme={darkMode ? 'dark' : 'light'}>
                  <label htmlFor="time">Jam</label>
                  <input
                    type="time"
                    id="time"
                    name="time"
                    value={formData.time}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>
              </FormRow>
              
              <FormGroup theme={darkMode ? 'dark' : 'light'}>
                <label htmlFor="notes">Catatan</label>
                <textarea
                  id="notes"
                  name="notes"
                  rows="4"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Deskripsi atau catatan untuk sesi konseling ini"
                  required
                ></textarea>
              </FormGroup>
              
              <FormActions>
                <SubmitButton type="submit" disabled={submitting}>
                  {submitting ? 'Mengirim...' : 'Kirim'}
                </SubmitButton>
              </FormActions>
            </form>
          </FormWrapper>
        </FormContainer>
      )}
    </PageContainer>
  );
};

const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
  
  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
  }
  
  p {
    color: var(--text-light);
  }
`;

const FormContainer = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: var(--box-shadow);
  transition: background-color 0.3s, box-shadow 0.3s;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const FormWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--text-dark);
  }
  
  input, select, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-family: inherit;
    font-size: 1rem;
    color: var(--text-dark);
    background-color: ${props => props.theme === 'dark' ? '#383838' : '#f5f5f5'};
    
    &:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 2px rgba(43, 93, 161, 0.2);
      outline: none;
    }
  }
  
  textarea {
    resize: vertical;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  ${FormGroup} {
    flex: 1;
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const SubmitButton = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: var(--primary-light);
  }
`;

const SuccessMessage = styled.div`
  background-color: #e6f8f5;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: var(--box-shadow);
  display: flex;
  align-items: center;
  gap: 1.5rem;
  
  h3 {
    color: #00a389;
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
  
  p {
    color: var(--text-dark);
  }
  
  @media (max-width: 576px) {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }
`;

const SuccessIcon = styled.div`
  width: 60px;
  height: 60px;
  background-color: #00a389;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: bold;
`;

const SuggestionsList = styled.ul`
  position: absolute;
  top: calc(100% - 0rem);
  left: 0;
  right: 0;
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: ${props => props.theme === 'dark' ? '#383838' : 'white'};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
`;

const SuggestionItem = styled.li`
  padding: 0.75rem 1rem;
  cursor: pointer;
  color: ${props => props.theme === 'dark' ? '#fff' : '#333'};

  &:hover {
    background-color: ${props => props.theme === 'dark' ? '#4a4a4a' : '#f5f5f5'};
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }
`;

const StudentSuggestionItem = styled(SuggestionItem)`
  padding: 0.5rem 1rem;
  
  .student-name {
    font-weight: 500;
    margin-bottom: 0.25rem;
  }
  
  .student-info {
    font-size: 0.875rem;
    color: ${props => props.theme === 'dark' ? '#aaa' : '#666'};
  }
`;

// Notification Toast Components (copied from LaporKTSPage.js for consistency)
const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  min-width: 300px;
  background-color: ${props => props.$type === 'success' ? '#2ecc71' : '#e74c3c'};
  color: white;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 9999;
  animation: slideIn 0.3s ease-out forwards;
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
`;

const NotificationMessage = styled.div`
  flex: 1;
  font-weight: 500;
`;

const CloseNotificationButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  margin-left: 10px;
  opacity: 0.8;
  transition: opacity 0.2s;
  &:hover { opacity: 1; }
`;

export default KonselingPage;